import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/components/AuthProvider';
import { getCurrencySymbol } from '@/lib/utils';

export type Booking = {
  id: string;
  company: string;
  role: string;
  bookedDates: string;
  startDate: string;
  endDate: string;
  earnings: string;
  status: string;
  days: number;
  rate: string;
  rateType: 'daily' | 'hourly';
  hoursPerDay?: number;
  dayHours?: Record<string, number>;
  notes?: string;
};

export type BookingDraft = {
  company: string;
  role: string;
  startDate: string;
  endDate: string;
  days: number;
  rate: string;
  rateType: 'daily' | 'hourly';
  hoursPerDay?: number;
  dayHours?: Record<string, number>;
  status: string;
  notes?: string;
};

// Short display format for booking row: MM/DD (no year) — matches original design
const fmtDate = (iso: string) => { const [, m, d] = iso.split('-'); return `${m}/${d}`; };
const fmtDateRange = (start: string, end: string): string => {
  if (!start || !end) return '';
  return `${fmtDate(start)} - ${fmtDate(end)}`;
};

const parseNum = (s: string): number => parseFloat(s.replace(/[^0-9.]/g, '')) || 0;

function dbToBooking(row: Record<string, unknown>, sym: string = '$'): Booking {
  const rate = Number(row.rate) || 0;
  const rateType: 'daily' | 'hourly' = (row.rate_type as string) === 'hourly' ? 'hourly' : 'daily';
  const earnings = Number(row.earnings) || 0;
  return {
    id: row.id as string,
    company: (row.company as string) || '',
    role: (row.role as string) || '',
    startDate: (row.start_date as string) || '',
    endDate: (row.end_date as string) || '',
    bookedDates: fmtDateRange(row.start_date as string, row.end_date as string),
    days: Number(row.days) || 0,
    rate: `${sym}${rate}${rateType === 'daily' ? '/day' : '/hr'}`,
    rateType,
    hoursPerDay: Number(row.hours_per_day) || 8,
    dayHours: (row.day_hours as Record<string, number>) || undefined,
    earnings: `${sym}${earnings.toLocaleString()}`,
    status: (row.status as string) || 'Active',
    notes: (row.notes as string) || undefined,
  };
}

export function useBookings(currency?: string) {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const sym = getCurrencySymbol(currency ?? 'USD');

  const refresh = useCallback(async () => {
    if (!user) { setBookings([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('user_id', user.id)
      .eq('archived', false)
      .order('start_date', { ascending: false });
    if (!error && data) setBookings((data as Record<string, unknown>[]).map(r => dbToBooking(r, sym)));
    setLoading(false);
  }, [user, sym]);

  useEffect(() => { refresh(); }, [refresh]);

  const addBooking = async (draft: BookingDraft, earningsStr: string): Promise<void> => {
    if (!user) return;
    const { data, error } = await supabase.from('bookings').insert({
      user_id: user.id,
      company: draft.company,
      role: draft.role,
      start_date: draft.startDate,
      end_date: draft.endDate,
      days: draft.days,
      rate: parseNum(draft.rate),
      rate_type: draft.rateType,
      hours_per_day: draft.hoursPerDay ?? 8,
      day_hours: draft.dayHours && Object.keys(draft.dayHours).length > 0 ? draft.dayHours : null,
      earnings: parseNum(earningsStr),
      status: draft.status,
      notes: draft.notes || null,
      import_source: 'manual',
    }).select().single();
    if (error) {
      console.error('[useBookings] addBooking failed:', error);
      throw error;
    }
    if (data) {
      setBookings(prev => [dbToBooking(data as Record<string, unknown>, sym), ...prev]);
    }
  };

  const updateBooking = async (id: string, draft: BookingDraft, earningsStr: string): Promise<void> => {
    if (!user) return;
    let recalcEarnings: number;
    if (draft.dayHours && Object.keys(draft.dayHours).length > 0) {
      recalcEarnings = parseNum(earningsStr);
    } else if (draft.rateType === 'hourly') {
      recalcEarnings = draft.days * (draft.hoursPerDay ?? 8) * parseNum(draft.rate);
    } else {
      recalcEarnings = draft.days * parseNum(draft.rate);
    }
    const { data, error } = await supabase.from('bookings').update({
      company: draft.company,
      role: draft.role,
      start_date: draft.startDate,
      end_date: draft.endDate,
      days: draft.days,
      rate: parseNum(draft.rate),
      rate_type: draft.rateType,
      hours_per_day: draft.hoursPerDay ?? 8,
      day_hours: draft.dayHours && Object.keys(draft.dayHours).length > 0 ? draft.dayHours : null,
      earnings: recalcEarnings,
      status: draft.status,
      notes: draft.notes || null,
    }).eq('id', id).eq('user_id', user.id).select().single();
    if (error) {
      console.error('[useBookings] updateBooking failed:', error);
      throw error;
    }
    if (data) {
      setBookings(prev => prev.map(b => b.id === id ? dbToBooking(data as Record<string, unknown>, sym) : b));
    }
  };

  const deleteBooking = async (id: string): Promise<void> => {
    if (!user) return;
    const previousBookings = bookings;
    setBookings(prev => prev.filter(b => b.id !== id));
    const { error } = await supabase.from('bookings').delete().eq('id', id).eq('user_id', user.id);
    if (error) {
      setBookings(previousBookings);
      console.error('[useBookings] deleteBooking failed:', error);
      throw error;
    }
  };

  const updateStatus = async (id: string, status: string): Promise<void> => {
    if (!user) return;
    const previousBookings = bookings;
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    const { error } = await supabase.from('bookings').update({ status }).eq('id', id).eq('user_id', user.id);
    if (error) {
      setBookings(previousBookings);
      console.error('[useBookings] updateStatus failed:', error);
      throw error;
    }
  };

  return { bookings, loading, addBooking, updateBooking, deleteBooking, updateStatus, refresh };
}
